/**
 * Cart bin label data matrix payload scanned on the kitting pick page.
 *
 * Format: `KIT|{slotKey}` where slotKey is the stable cart slot id.
 * Operators may also scan the printed bin letter A through G when it
 * matches an active cart mark.
 */

export const KITTING_CART_LABEL_SCAN_PREFIX = "KIT|";

export function buildKittingCartLabelScanPayload(slotKey: string): string {
  const trimmed = slotKey.trim();
  if (trimmed.length === 0) {
    throw new Error("slotKey is required for cart label scan payload.");
  }
  return `${KITTING_CART_LABEL_SCAN_PREFIX}${trimmed}`;
}

function isKittingCartLetter(value: string): boolean {
  return value.length === 1 && value >= "A" && value <= "G";
}

export interface KittingCartScanResolution {
  slotKey: string;
  /** When the scan was a bare bin letter rather than a full payload. */
  matchedByLetter: boolean;
}

export function resolveKittingCartScanToSlotKey(
  scanned: string,
  activeSlotIds: readonly string[],
  letterToSlotKey?: ReadonlyMap<string, string>,
): KittingCartScanResolution | null {
  const trimmed = scanned.trim();
  if (trimmed.length === 0) return null;

  if (trimmed.startsWith(KITTING_CART_LABEL_SCAN_PREFIX)) {
    const slotKey = trimmed.slice(KITTING_CART_LABEL_SCAN_PREFIX.length).trim();
    if (slotKey.length > 0 && activeSlotIds.includes(slotKey)) {
      return { slotKey, matchedByLetter: false };
    }
    return null;
  }

  const letter = trimmed.toUpperCase();
  if (isKittingCartLetter(letter) && letterToSlotKey != null) {
    const slotKey = letterToSlotKey.get(letter);
    if (slotKey != null && activeSlotIds.includes(slotKey)) {
      return { slotKey, matchedByLetter: true };
    }
  }

  if (activeSlotIds.includes(trimmed)) {
    return { slotKey: trimmed, matchedByLetter: false };
  }

  return null;
}
